import React, { useEffect, useState } from 'react';
import { timeTracking as timeApi } from '@/utils/api';
import { Plus, Trash2, Clock, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function TimeTab({ projectId }) {
  const [data, setData] = useState({ entries: [], total_hours: 0, billable_hours: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ description: '', hours: 0, date: new Date().toISOString().slice(0, 10), billable: true, rate: 150 });

  const reload = () => timeApi.list(projectId).then(r => setData(r.data)).finally(() => setLoading(false));
  useEffect(() => { reload(); }, [projectId]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.hours) return toast.error('Description & hours required');
    try {
      await timeApi.create(projectId, form);
      setForm({ description: '', hours: 0, date: new Date().toISOString().slice(0, 10), billable: true, rate: form.rate });
      reload();
      toast.success('Time logged');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete time entry?')) return;
    await timeApi.delete(projectId, id);
    reload();
  };

  return (
    <div className="animate-fade-in" data-testid="time-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Time Tracking</h3>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="card-base p-3">
          <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--muted)' }}><Clock size={12} /> Total hours</div>
          <p className="text-2xl font-black" style={{ color: 'var(--ink)' }}>{data.total_hours}</p>
        </div>
        <div className="card-base p-3">
          <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--muted)' }}><DollarSign size={12} /> Billable</div>
          <p className="text-2xl font-black" style={{ color: '#3C8A5A' }}>{data.billable_hours}</p>
        </div>
        <div className="card-base p-3">
          <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--muted)' }}>Revenue</div>
          <p className="text-2xl font-black" style={{ color: 'var(--ink)' }}>${data.total_amount.toLocaleString()}</p>
        </div>
      </div>

      {/* Add row */}
      <form onSubmit={add} className="card-base p-3 mb-4">
        <div className="grid grid-cols-12 gap-2 items-center">
          <input className="input-agency text-sm col-span-5" placeholder="Task description…" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="time-description" />
          <input className="input-agency text-sm col-span-2" type="number" step="0.25" min="0" placeholder="Hrs" value={form.hours} onChange={e => setForm({...form, hours: Number(e.target.value)})} data-testid="time-hours" />
          <input className="input-agency text-sm col-span-2" type="number" min="0" placeholder="Rate" value={form.rate} onChange={e => setForm({...form, rate: Number(e.target.value)})} />
          <input className="input-agency text-sm col-span-2" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
          <button type="submit" className="btn-primary text-xs col-span-1 py-2 flex items-center justify-center" data-testid="add-time-entry"><Plus size={14} /></button>
        </div>
        <label className="flex items-center gap-2 text-xs mt-2" style={{ color: 'var(--muted)' }}>
          <input type="checkbox" checked={form.billable} onChange={e => setForm({...form, billable: e.target.checked})} />
          Billable
        </label>
      </form>

      {/* Entries */}
      {loading ? (
        <div className="h-16 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />
      ) : data.entries.length === 0 ? (
        <div className="card-base p-6 text-center">
          <Clock size={28} style={{ color: 'var(--border)', margin: '0 auto 8px' }} />
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No time logged yet.</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="time-entries">
          {data.entries.map(t => (
            <div key={t.id} className="card-base p-3 flex items-center gap-3" data-testid={`time-entry-${t.id}`}>
              <div className="text-xs font-mono flex-shrink-0" style={{ color: 'var(--muted)', width: 90 }}>{t.date}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate" style={{ color: 'var(--ink)' }}>{t.description}</p>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>{t.user_name || 'Operator'} · {t.billable ? 'billable' : 'non-billable'}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold tabular-nums" style={{ color: 'var(--ink)' }}>{t.hours}h</p>
                {t.billable && t.rate > 0 && (
                  <p className="text-xs tabular-nums" style={{ color: '#3C8A5A' }}>${(t.hours * t.rate).toFixed(2)}</p>
                )}
              </div>
              <button onClick={() => remove(t.id)} className="p-1 transition-colors" style={{ color: '#B34A3A' }} data-testid={`delete-time-${t.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
